import React, { useState, useEffect } from 'react';
import { dashboardAPI, syncAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

function StatCard({ label, value, sub, color }) {
  const colors = {
    blue:   'bg-blue-50 border-blue-200 text-blue-700',
    red:    'bg-red-50 border-red-200 text-red-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    orange: 'bg-orange-50 border-orange-200 text-orange-700',
    green:  'bg-green-50 border-green-200 text-green-700',
    purple: 'bg-purple-50 border-purple-200 text-purple-700',
    teal:   'bg-teal-50 border-teal-200 text-teal-700',
    slate:  'bg-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <div className={`rounded-xl border p-5 ${colors[color] || colors.slate}`}>
      <p className="text-3xl font-bold">{value ?? '—'}</p>
      <p className="font-semibold text-sm mt-1">{label}</p>
      {sub && <p className="text-xs mt-1 opacity-70">{sub}</p>}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-slate-800">{title}</h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">{children}</div>
    </div>
  );
}

export default function ComplianceDashboard() {
  const { admin }           = useAuth();
  const [stats, setStats]   = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [syncMsg, setSyncMsg] = useState('');
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    Promise.all([
      dashboardAPI.stats(),
      syncAPI.status().catch(() => ({ data: null })),
    ]).then(([statsRes, syncRes]) => {
      setStats(statsRes.data?.data);
      setSyncStatus(syncRes.data?.data);
    }).catch((err) => {
      setError(err.response?.data?.message || 'Failed to load dashboard data.');
    }).finally(() => setLoading(false));
  }, []);

  async function runSync() {
    setSyncing(true); setSyncMsg(''); setError('');
    try {
      const res = await syncAPI.bulkPush();
      const synced = res.data?.data?.synced ?? 0;
      setSyncMsg(`Sync complete — ${synced} employee${synced !== 1 ? 's' : ''} pushed to LMS.`);
      const syncRes = await syncAPI.status().catch(() => ({ data: null }));
      setSyncStatus(syncRes.data?.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Sync failed.');
    } finally { setSyncing(false); }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const d = stats || {};

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Compliance Dashboard</h1>
          <p className="text-slate-500 text-sm mt-1">Real-time overview of document compliance, pipeline status, and system health.</p>
        </div>
        <button
          onClick={() => window.location.reload()}
          className="border border-slate-300 text-slate-600 px-4 py-2 rounded-lg text-sm hover:bg-slate-50 transition-colors"
        >
          Refresh
        </button>
      </div>

      {error   && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-4 py-3 text-sm">{error}</div>}
      {syncMsg && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg px-4 py-3 text-sm">{syncMsg}</div>}

      {/* Expiring Documents */}
      <Section title="Expiring Documents">
        <StatCard label="Expiring Soon (30d)" value={d?.documents?.expiring_soon} color="orange" />
        <StatCard label="Already Expired"     value={d?.documents?.expired}       color="red" />
        <StatCard label="Missing Documents"   value={d?.documents?.missing}       color="yellow" />
        <StatCard label="Total Employees"     value={d?.employees?.total}         color="slate" />
      </Section>

      {/* Audit Readiness */}
      <Section title="Audit Readiness">
        <StatCard label="Audit Ready"      value={d?.audit?.ready}      color="green" />
        <StatCard label="Not Ready"        value={d?.audit?.not_ready}  color="red" />
        <StatCard label="Readiness %"      value={d?.audit?.percentage != null ? `${d.audit.percentage}%` : null} color="blue" />
        <StatCard label="Total Active"     value={d?.audit?.total}      color="slate" />
      </Section>

      {/* Pipeline */}
      <Section title="Candidate Pipeline">
        <StatCard label="Total Candidates" value={d?.candidates?.total}              color="slate" />
        <StatCard label="Offer Pending"    value={d?.candidates?.offers_pending}     color="purple" />
        <StatCard label="I-9 Pending"      value={d?.candidates?.i9_pending}         color="orange" />
        <StatCard label="Onboarding"       value={d?.candidates?.onboarding_pending} color="blue" />
      </Section>

      {/* LMS Sync */}
      <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-lg font-semibold text-slate-800">LMS Sync Status</h2>
            <p className="text-sm text-slate-500 mt-0.5">Employees synced to the Learning Management System.</p>
          </div>
          {(admin?.role === 'super_admin' || admin?.role === 'compliance_admin') && (
            <button
              onClick={runSync}
              disabled={syncing}
              className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {syncing ? 'Syncing...' : 'Run Sync Now'}
            </button>
          )}
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <StatCard label="Total CP Employees" value={d?.employees?.total}                color="slate" />
          <StatCard label="Synced to LMS"      value={syncStatus?.employees_synced}       color="green" />
          <StatCard label="Sync Failed"        value={syncStatus?.employees_failed}       color="red" />
          <StatCard
            label="Last Sync"
            value={syncStatus?.last_sync_at
              ? new Date(syncStatus.last_sync_at).toLocaleDateString()
              : '—'}
            color="blue"
          />
        </div>
        {syncStatus && (d?.employees?.total ?? 0) > (syncStatus?.employees_synced ?? 0) && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
            {(d.employees.total - syncStatus.employees_synced)} employee{d.employees.total - syncStatus.employees_synced !== 1 ? 's' : ''} in CP not yet synced to LMS — click "Run Sync Now" to push them.
          </p>
        )}
      </div>
    </div>
  );
}
